import React, { useState } from 'react';
import KeystoneLogo from '../components/KeystoneLogo';
import { downloadServerPDF } from '../lib/pdf-download';

const COLORS = {
  navy: '#0A2540',
  navyDeep: '#061A2E',
  navyMid: '#0F3A5F',
  magenta: '#E91E63',
  gold: '#C9A961',
  cream: '#FAF8F5',
  paper: '#FFFFFF',
  ink: '#1A2332',
  slate: '#4A5568',
  muted: '#6B7C8E',
  border: '#E5E1D8',
  green: '#16A34A',
};

function PrintButton() {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadServerPDF('/founding-customer', 'Keystone-Founding-Customer-Programme.pdf');
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('PDF export failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="no-print" style={{ position: 'fixed', top: 24, right: 24, zIndex: 100, display: 'flex', gap: 10, alignItems: 'center' }}>
      <a
        href="/"
        data-testid="founding-back-to-site"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '12px 16px',
          background: '#fff',
          color: COLORS.navy,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: '0.02em',
          textDecoration: 'none',
        }}
      >
        ← Back to site
      </a>
      <button
        onClick={() => window.print()}
        data-testid="founding-print-btn"
        style={{
          padding: '12px 20px',
          background: '#fff',
          color: COLORS.navy,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: '0.04em',
          cursor: 'pointer',
        }}
      >
        🖨 Print
      </button>
      <button
        onClick={handleDownload}
        disabled={downloading}
        data-testid="founding-download-pdf-btn"
        style={{
          padding: '14px 24px',
          background: downloading ? COLORS.muted : COLORS.navy,
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: '0.04em',
          cursor: downloading ? 'wait' : 'pointer',
          boxShadow: '0 12px 28px rgba(10,37,64,0.25)',
          textTransform: 'uppercase',
        }}
      >
        {downloading ? '⏳ Generating…' : '⬇ Download PDF'}
      </button>
    </div>
  );
}

const HEADER_BAND = (
  <div style={{ borderBottom: `3px solid ${COLORS.navy}`, paddingBottom: '8mm', marginBottom: '10mm' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
      <KeystoneLogo height={42} variant="light" showTagline={false} />
      <div style={{
        fontSize: '8.5pt', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700,
        color: COLORS.magenta,
      }}>
        Founding Customer Programme · Confidential
      </div>
    </div>
  </div>
);

export default function FoundingCustomerPDF() {
  return (
    <>
      <PrintButton />

      <div style={{
        background: '#E8E5DE', minHeight: '100vh',
        padding: '40px 0', boxSizing: 'border-box',
      }} className="page-shell">
        {/* PAGE 1 */}
        <div className="brochure-page" style={{
          width: '210mm', minHeight: '297mm',
          background: COLORS.paper,
          margin: '0 auto 24px',
          padding: '20mm 18mm',
          boxShadow: '0 10px 40px rgba(0,0,0,0.10)',
          fontFamily: "'Inter', system-ui, sans-serif",
          color: COLORS.ink,
          boxSizing: 'border-box',
          position: 'relative',
        }}>
          {HEADER_BAND}

          <div style={{ marginTop: '6mm' }}>
            <div style={{ fontSize: '9pt', color: COLORS.magenta, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: '4mm' }}>
              For Irish artisan food makers · limited founding-partner cohort
            </div>
            <h1 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: '38pt', fontWeight: 800,
              color: COLORS.navy, lineHeight: 1.0, letterSpacing: '-0.03em',
              margin: 0,
            }}>
              The Founding<br/>Customer Programme.
            </h1>
            <p style={{ fontSize: '12pt', lineHeight: 1.55, color: COLORS.slate, marginTop: '6mm', maxWidth: '155mm' }}>
              Up to five Irish artisan food makers will help shape Keystone for their industry. Cashel Farmhouse Cheesemakers became the first, going live in February 2026. The remaining four founding-partner places are open. Each gets the product at a price no future customer will ever see, direct access to the build team, and their name on the founding plaque.
            </p>

            {/* Cashel Blue founding customer */}
            <div style={{
              marginTop: '6mm',
              padding: '5mm 6mm',
              background: COLORS.cream,
              border: `1px solid ${COLORS.border}`,
              borderLeft: `3px solid ${COLORS.gold}`,
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              gap: '6mm',
            }}>
              <img
                src="/customers/cashel-blue-logo.webp"
                alt="Cashel Farmhouse Cheesemakers"
                style={{ height: 56, width: 'auto', flexShrink: 0, objectFit: 'contain' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '7.5pt', color: COLORS.gold, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: '1mm' }}>
                  Founding customer · live deployment
                </div>
                <div style={{ fontSize: '11pt', fontWeight: 700, color: COLORS.navy, lineHeight: 1.35 }}>
                  Founding customer with <strong style={{ color: COLORS.navy }}>Cashel Farmhouse Cheesemakers</strong>, Tipperary, live on Keystone since February 2026, dispatching across an international distribution footprint. Brand name used with the written permission of J&L Grubb Ltd.
                </div>
              </div>
            </div>
          </div>

          {/* The deal */}
          <div style={{
            marginTop: '10mm',
            background: COLORS.navy, color: '#fff',
            borderRadius: 6, padding: '10mm 12mm',
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', top: 0, right: 0,
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: '120pt', fontWeight: 800, color: COLORS.gold, opacity: 0.08,
              lineHeight: 1, letterSpacing: '-0.05em', padding: '4mm 10mm 0 0',
            }}>40%</div>
            <div style={{ position: 'relative' }}>
              <div style={{ fontSize: '8.5pt', color: COLORS.gold, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: '4mm' }}>
                The deal, locked for three years
              </div>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr',
                gap: '8mm', marginTop: '4mm',
              }}>
                <div>
                  <div style={{ fontSize: '8pt', color: COLORS.gold, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em' }}>Licence, 3-year stair-step</div>
                  <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '22pt', fontWeight: 800, color: '#fff', lineHeight: 1.05, marginTop: '2mm', letterSpacing: '-0.01em' }}>
                    40% / 30% / 30%
                  </div>
                  <div style={{ fontSize: '9.5pt', color: '#CBD5E0', marginTop: '3mm', lineHeight: 1.55 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.10)', padding: '2mm 0' }}>
                      <span><strong style={{ color: '#fff' }}>Year 1</strong> · 40% off</span>
                      <span style={{ color: COLORS.gold, fontWeight: 700 }}>€474/mo</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.10)', padding: '2mm 0' }}>
                      <span><strong style={{ color: '#fff' }}>Years 2–3</strong> · 30% off</span>
                      <span style={{ color: COLORS.gold, fontWeight: 700 }}>€553/mo</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2mm 0' }}>
                      <span><strong style={{ color: '#fff' }}>Year 4+</strong> · standard</span>
                      <span style={{ color: '#94A3B8' }}>€790/mo</span>
                    </div>
                  </div>
                  <div style={{ fontSize: '9pt', color: COLORS.gold, marginTop: '3mm', fontWeight: 600, fontStyle: 'italic' }}>
                    ~€10,700 saving over three years.
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '8pt', color: COLORS.gold, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em' }}>Implementation</div>
                  <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '22pt', fontWeight: 800, color: '#fff', lineHeight: 1.05, marginTop: '2mm' }}>
                    Half price
                  </div>
                  <div style={{ fontSize: '10pt', color: '#CBD5E0', marginTop: '2mm' }}>
                    €4,250–€8,250 (typically €5,500). Same scope, same fixed fee, same 30-day hand-holding.
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '8pt', color: COLORS.gold, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em' }}>Bolt-ons</div>
                  <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20pt', fontWeight: 800, color: '#fff', lineHeight: 1.05, marginTop: '2mm' }}>
                    25% off year one
                  </div>
                  <div style={{ fontSize: '10pt', color: '#CBD5E0', marginTop: '2mm' }}>
                    Accounts package integration, retailer EDI, custom modules. Useful if you go live and add Tesco in month four.
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '8pt', color: COLORS.gold, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em' }}>Direct line</div>
                  <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20pt', fontWeight: 800, color: '#fff', lineHeight: 1.05, marginTop: '2mm' }}>
                    Build team, not support
                  </div>
                  <div style={{ fontSize: '10pt', color: '#CBD5E0', marginTop: '2mm' }}>
                    Founder mobile number, direct WhatsApp/Teams to the engineers. For the full three years.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Why we're doing this */}
          <div style={{ marginTop: '10mm' }}>
            <div style={{ fontSize: '9pt', color: COLORS.magenta, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: '3mm' }}>
              Why we're doing this
            </div>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20pt', fontWeight: 700, color: COLORS.navy, margin: 0, lineHeight: 1.15, letterSpacing: '-0.01em' }}>
              Because Irish artisan food can't be built from a generic ERP demo.
            </h2>
            <p style={{ fontSize: '10pt', lineHeight: 1.6, color: COLORS.slate, marginTop: '4mm' }}>
              We've spent fifteen years building enterprise systems. We know what good production software looks like. What we don't know yet, and what no Dublin software office can tell us, is exactly how a Tipperary cheesemaker hands a Friday-afternoon dispatch to a DPD driver, exactly how a Galway smoked-fish producer reconciles weekly retailer credit notes, exactly how a West Cork creamery proves to Tesco that yesterday's batch is safe.
            </p>
            <p style={{ fontSize: '10pt', lineHeight: 1.6, color: COLORS.slate, marginTop: '4mm' }}>
              Up to five founding partners will run with us as we learn, Cashel Blue is the first; four remain. In return, we make Keystone the system that fits, not the system that <em>almost</em> fits, the way every "industry vertical" piece of software almost fits today.
            </p>
          </div>

          {/* Footer */}
          <div style={{
            position: 'absolute', bottom: '14mm', left: '18mm', right: '18mm',
            paddingTop: '4mm', borderTop: `1px solid ${COLORS.border}`,
            display: 'flex', justifyContent: 'space-between',
            fontSize: '8pt', color: COLORS.muted,
          }}>
            <span>Keystone by CloudVoro · Founding Customer Programme</span>
            <span>Page 1 / 2</span>
          </div>
        </div>

        {/* PAGE 2 */}
        <div className="brochure-page" style={{
          width: '210mm', minHeight: '297mm',
          background: COLORS.paper,
          margin: '0 auto 24px',
          padding: '20mm 18mm',
          boxShadow: '0 10px 40px rgba(0,0,0,0.10)',
          fontFamily: "'Inter', system-ui, sans-serif",
          color: COLORS.ink,
          boxSizing: 'border-box',
          position: 'relative',
        }}>
          {HEADER_BAND}

          {/* What we ask in return */}
          <div style={{ marginTop: '4mm' }}>
            <div style={{ fontSize: '9pt', color: COLORS.magenta, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: '3mm' }}>
              What we ask in return
            </div>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20pt', fontWeight: 700, color: COLORS.navy, margin: 0, lineHeight: 1.15, letterSpacing: '-0.01em' }}>
              Honesty, time, and your name on the case study.
            </h2>
            <ol style={{ paddingLeft: '5mm', margin: '6mm 0 0', fontSize: '10.5pt', lineHeight: 1.7, color: COLORS.ink }}>
              <li style={{ marginBottom: '4mm' }}>
                <strong style={{ color: COLORS.navy }}>Two scoping days on your floor.</strong> We'll need 4–6 hours with the operations lead, the QC manager, and whoever currently lives inside the dispatch spreadsheet. We pay for this, it's part of the implementation.
              </li>
              <li style={{ marginBottom: '4mm' }}>
                <strong style={{ color: COLORS.navy }}>Structured monthly feedback for 6 months.</strong> A 30-minute call where you tell us what's broken, what's missing, what's brilliant. We act on it within the same sprint cycle, usually inside two weeks.
              </li>
              <li style={{ marginBottom: '4mm' }}>
                <strong style={{ color: COLORS.navy }}>A signed case study at month 6.</strong> One page, one quote, two photos. We write the draft, you redline it. We use it on the website, sales materials, and Bord Bia / Dairy Connect events. You get final sign-off on every word.
              </li>
              <li style={{ marginBottom: '4mm' }}>
                <strong style={{ color: COLORS.navy }}>One reference call per quarter</strong> with a serious prospective buyer in your sector. Maximum 30 minutes. We brief you in advance, never blind-side you.
              </li>
            </ol>
          </div>

          {/* Eligibility */}
          <div style={{
            marginTop: '8mm',
            padding: '8mm 10mm',
            background: COLORS.cream,
            border: `1px solid ${COLORS.border}`,
            borderLeft: `4px solid ${COLORS.gold}`,
            borderRadius: 4,
          }}>
            <div style={{ fontSize: '9pt', color: COLORS.gold, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: '3mm' }}>
              Eligibility
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6mm 10mm', fontSize: '10pt', lineHeight: 1.6, color: COLORS.ink }}>
              <div>
                <strong style={{ color: COLORS.navy }}>You should be:</strong>
                <ul style={{ paddingLeft: '4mm', margin: '2mm 0 0' }}>
                  <li>An Irish or UK artisan food producer (any specialism)</li>
                  <li>Currently selling into at least one retail or foodservice channel</li>
                  <li>Running on Excel, Sage / SurfAccounts, or a generic ERP</li>
                  <li>Producing 50–500 batches a month</li>
                </ul>
              </div>
              <div>
                <strong style={{ color: COLORS.navy }}>You should be willing to:</strong>
                <ul style={{ paddingLeft: '4mm', margin: '2mm 0 0' }}>
                  <li>Move your live operation to Keystone within 8 weeks</li>
                  <li>Give honest, sometimes uncomfortable feedback</li>
                  <li>Have your name on the founding plaque (a real plaque)</li>
                  <li>Take occasional reference calls from prospective customers</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Channel partner note */}
          <div style={{ marginTop: '8mm' }}>
            <div style={{ fontSize: '9pt', color: COLORS.magenta, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: '3mm' }}>
              Coming via Bord Bia, Dairy Connect, IFSC or Teagasc?
            </div>
            <p style={{ fontSize: '10pt', lineHeight: 1.6, color: COLORS.slate, margin: 0 }}>
              Channel-partner rates apply automatically, no separate paperwork. Just mention the programme on the discovery call. Founding spots are first-come, first-served regardless of channel.
            </p>
          </div>

          {/* How to apply */}
          <div style={{
            marginTop: '8mm',
            background: COLORS.navy, color: '#fff',
            borderRadius: 6, padding: '8mm 10mm',
          }}>
            <div style={{ fontSize: '9pt', color: COLORS.gold, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: '3mm' }}>
              How to apply
            </div>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
              gap: '6mm', marginTop: '4mm',
            }}>
              {[
                { n: '1', t: 'Email or call', d: 'hello@cloudvoro.com / +353 85 181 0310. Tell us about your operation in 4–5 lines.' },
                { n: '2', t: '60-min discovery', d: 'We listen, you talk. If we\'re not the right fit, we say so on the call.' },
                { n: '3', t: 'Written brief in 48h', d: 'Fixed-fee implementation scope. Founding rates locked in. You decide.' },
              ].map(s => (
                <div key={s.n}>
                  <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '32pt', fontWeight: 800, color: COLORS.gold, lineHeight: 1, letterSpacing: '-0.03em' }}>{s.n}</div>
                  <div style={{ fontSize: '11pt', fontWeight: 700, color: '#fff', marginTop: '2mm' }}>{s.t}</div>
                  <div style={{ fontSize: '9pt', color: '#CBD5E0', marginTop: '2mm', lineHeight: 1.5 }}>{s.d}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Trust line */}
          <div style={{
            marginTop: '8mm',
            padding: '5mm 6mm',
            background: COLORS.cream,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 4,
            fontSize: '9pt', color: COLORS.slate, lineHeight: 1.55, fontStyle: 'italic',
          }}>
            <strong style={{ color: COLORS.navy, fontStyle: 'normal' }}>One small thing.</strong> Founding rates and the founding plaque are honoured for the lifetime of your account. If you sell your business, the rates transfer to the new owner. If we're acquired, the rates transfer with us. We've put this in writing in the contract because we mean it.
          </div>

          {/* Footer */}
          <div style={{
            position: 'absolute', bottom: '14mm', left: '18mm', right: '18mm',
            paddingTop: '4mm', borderTop: `1px solid ${COLORS.border}`,
            display: 'flex', justifyContent: 'space-between',
            fontSize: '8pt', color: COLORS.muted,
          }}>
            <span>Keystone by CloudVoro · Founding Customer Programme · hello@cloudvoro.com · +353 85 181 0310</span>
            <span>Page 2 / 2</span>
          </div>
        </div>
      </div>

      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          body, html { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          .page-shell { background: #fff !important; padding: 0 !important; }
          .no-print { display: none !important; }
          .brochure-page {
            box-shadow: none !important;
            margin: 0 !important;
            page-break-after: always;
            page-break-inside: avoid;
          }
          .brochure-page:last-child,
          .brochure-page:last-of-type {
            page-break-after: auto !important;
            break-after: auto !important;
          }
        }
      `}</style>
    </>
  );
}
